import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { useHelpTypography } from '../../hooks/useHelpTypography';
import { helpI18n } from '../../i18n/help';

type CalloutVariant = 'info' | 'warning' | 'tip';

type GuideStep = {
  title: string;
  bullets?: string[];
  body?: string;
};

type ContentBlock =
  | { type: 'section'; title: string; body?: string; steps?: string[]; bullets?: string[] }
  | { type: 'reportDetail'; body: string; bullets?: string[] }
  | { type: 'guide'; steps: GuideStep[] }
  | { type: 'reference'; intro: string; items: string[] }
  | { type: 'steps'; steps: string[] }
  | { type: 'bullets'; intro?: string; items: string[] }
  | { type: 'callout'; variant: CalloutVariant; title?: string; body?: string; bullets?: string[] }
  | { type: 'paragraph'; text: string };

const getLines = (block: string) =>
  block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const CALLOUT_TITLE_PATTERN =
  /^(warning|important|best practice|note|tip|ملاحظة مهمة|تحذير|ملاحظة|نصيحة):?\s*(.*)$/i;

const stripStepPrefix = (line: string) => line.replace(/^\d+\.\s*/, '').trim();
const stripBulletPrefix = (line: string) => line.replace(/^•\s*/, '').trim();

const OPTIONS_SECTION_PATTERN = /^(options|available options|الخيارات|الخيارات المتاحة)$/i;

const parseLabeledItem = (item: string) => {
  const parts = item.split(/\s+[—–-]\s+/);
  if (parts.length >= 2) {
    return { label: parts[0].trim(), description: parts.slice(1).join(' — ').trim() };
  }
  return { label: item.trim(), description: undefined };
};

const getFillLevelPercent = (label: string): number | null => {
  const normalized = label.toLowerCase();

  if (/quarter|ربع/.test(normalized)) return 25;
  if (/half|نصف/.test(normalized) && !/three|ثلاثة/.test(normalized)) return 50;
  if (/three quarter|ثلاثة أرباع|three.quarter/i.test(normalized)) return 75;
  if (/^full\b|ممتلئة بالكامل|بالكامل/.test(normalized)) return 100;

  return null;
};

const isFillLevelBulletSet = (items: string[]) =>
  items.map((item) => getFillLevelPercent(parseLabeledItem(item).label)).filter((value) => value !== null)
    .length >= 3;

const isOptionsSection = (block: ContentBlock) =>
  block.type === 'section' && OPTIONS_SECTION_PATTERN.test(block.title.trim());

const hasLabeledBullets = (items: string[]) =>
  items.some((item) => /\s+[—–-]\s+/.test(item));

const getSectionIcon = (title: string): SvgIconComponent | null => {
  const normalized = title.toLowerCase();

  if (/general esg|الأداء البيئي العام/.test(normalized)) {
    return AssessmentOutlinedIcon;
  }
  if (/waste & circularity|circularity|gri 306|النفايات والتدوير/.test(normalized)) {
    return RecyclingOutlinedIcon;
  }
  if (/carbon avoidance|تجنّب الكربون|تجرب الكربون/.test(normalized)) {
    return Co2OutlinedIcon;
  }

  return null;
};

const SECTION_META_LINE_PATTERN =
  /^(definition|content|period|التعريف|المحتوى|الفترة|تعریف|مواد|مدت|परिभाषा|सामग्री|अवधि|সংজ্ঞা|বিষয়বস্তু|সময়কাল|නිර්වචනය|අන්තර්ගතය|කාලසීමාව|परिभाषा|सामग्री|अवधि)\s*[—–-]\s*(.+)$/i;

const WHATS_INSIDE_PATTERN =
  /^(what['’`]?s inside|what is inside|ماذا يتضمن|اندر کیا ہے|अंदर क्या है|ভেতরে কী আছে|ඇතුළත කුමක්ද|भित्र के छ)$/i;

const isSectionMetaLine = (line: string) => SECTION_META_LINE_PATTERN.test(line.trim());

const isWhatsInsideTitle = (title: string) => WHATS_INSIDE_PATTERN.test(title.trim());

const getMetaIcon = (label: string): SvgIconComponent => {
  const normalized = label.toLowerCase();
  if (/definition|التعريف|تعریف|परिभाषा|সংজ্ঞা|නිර්වචන/.test(normalized)) {
    return MenuBookOutlinedIcon;
  }
  if (/content|المحتوى|مواد|सामग्री|বিষয়বস্তু|අන්තර්ගත/.test(normalized)) {
    return DescriptionOutlinedIcon;
  }
  return CalendarMonthOutlinedIcon;
};

function SectionMetaBody({ body, variant = 'stack' }: { body: string; variant?: 'stack' | 'grid' }) {
  const theme = useTheme();
  const lines = body.split('\n').map((line) => line.trim()).filter(Boolean);
  const useMetaLayout = lines.every((line) => SECTION_META_LINE_PATTERN.test(line));

  if (!useMetaLayout) {
    return (
      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.65,
          color: 'text.secondary',
          whiteSpace: 'pre-line',
        }}
      >
        {body}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns:
          variant === 'grid' ? { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } : '1fr',
        gap: 1.25,
      }}
    >
      {lines.map((line) => {
        const match = line.match(SECTION_META_LINE_PATTERN);
        if (!match) return null;
        const label = match[1];
        const description = match[2];
        const MetaIcon = getMetaIcon(label);

        return (
          <Box
            key={line}
            sx={{
              display: 'flex',
              flexDirection: variant === 'grid' ? 'column' : 'row',
              alignItems: variant === 'grid' ? 'flex-start' : 'flex-start',
              gap: variant === 'grid' ? 1 : 1.25,
              p: variant === 'grid' ? 1.5 : 1.25,
              minHeight: variant === 'grid' ? 120 : undefined,
              borderRadius: '12px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#FFFFFF',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: variant === 'grid' ? '0 1px 2px rgba(15, 23, 42, 0.04)' : 'none',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.main}12`,
                color: 'primary.main',
              }}
            >
              <MetaIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  mb: 0.5,
                  fontSize: '0.6875rem',
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.55, color: 'text.secondary', fontSize: '0.8125rem' }}
              >
                {description}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const isTipParagraph = (text: string) => /^(choose|اختر|tip:|نصيحة)/i.test(text.trim());

const parseGuideStepBlock = (block: string): GuideStep => {
  const lines = getLines(block);
  const headerText = lines[0].replace(/^\d+\.\s*/, '').trim();
  const title = headerText.replace(/:$/, '').trim();
  const subLines = lines.slice(1);
  const subBullets = subLines.filter((line) => line.startsWith('•')).map(stripBulletPrefix);

  if (subBullets.length > 0) {
    return { title, bullets: subBullets };
  }

  if (subLines.length > 0) {
    return { title, body: subLines.join('\n') };
  }

  return { title };
};

const getCalloutVariant = (title: string): CalloutVariant => {
  const normalized = title.toLowerCase();
  if (normalized.includes('warning') || normalized.includes('تحذير')) return 'warning';
  if (normalized.includes('best practice') || normalized.includes('tip') || normalized.includes('نصيحة')) {
    return 'tip';
  }
  return 'info';
};

const parseContentBlock = (block: string): ContentBlock => {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { type: 'paragraph', text: block };
  }

  const calloutMatch = lines[0].match(CALLOUT_TITLE_PATTERN);
  if (calloutMatch) {
    const title = calloutMatch[1];
    const inlineBody = calloutMatch[2]?.trim();
    const rest = lines.slice(1);
    const bullets = rest.filter((line) => line.startsWith('•')).map(stripBulletPrefix);
    const body =
      inlineBody ||
      (rest.length > 0 && bullets.length === 0 ? rest.join('\n') : undefined);

    return {
      type: 'callout',
      variant: getCalloutVariant(title),
      title,
      body,
      bullets: bullets.length > 0 ? bullets : undefined,
    };
  }

  if (lines.every((line) => line.startsWith('•'))) {
    return { type: 'bullets', items: lines.map(stripBulletPrefix) };
  }

  if (lines[0].endsWith(':') && lines.length > 1) {
    const title = lines[0].slice(0, -1).trim();
    const rest = lines.slice(1);
    const numbered = rest.filter((line) => /^\d+\.\s/.test(line));
    const bullets = rest.filter((line) => line.startsWith('•')).map(stripBulletPrefix);

    if (numbered.length > 0 && numbered.length === rest.length) {
      return { type: 'section', title, steps: numbered.map(stripStepPrefix) };
    }

    if (bullets.length > 0) {
      const nonBullets = rest.filter((line) => !line.startsWith('•'));
      return {
        type: 'section',
        title,
        body: nonBullets.length > 0 ? nonBullets.join('\n') : undefined,
        bullets,
      };
    }

    if (rest.length === 1) {
      return { type: 'section', title, body: rest[0] };
    }

    return { type: 'section', title, body: rest.join('\n') };
  }

  const bulletIndex = lines.findIndex((line) => line.startsWith('•'));
  if (bulletIndex > 0) {
    return {
      type: 'bullets',
      intro: lines.slice(0, bulletIndex).join('\n'),
      items: lines.slice(bulletIndex).map(stripBulletPrefix),
    };
  }

  if (lines.length === 1 && /^\d+\.\s/.test(lines[0])) {
    return { type: 'steps', steps: [stripStepPrefix(lines[0])] };
  }

  return { type: 'paragraph', text: block };
};

const parseArticleContent = (content: string): ContentBlock[] => {
  const rawBlocks = content
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  const blocks: ContentBlock[] = [];
  let index = 0;

  while (index < rawBlocks.length) {
    const block = rawBlocks[index];
    const lines = getLines(block);

    const metaLinesInBlock = lines.filter(isSectionMetaLine);
    if (metaLinesInBlock.length > 0 && metaLinesInBlock.length === lines.length) {
      const metaLines = [...metaLinesInBlock];
      index += 1;

      while (index < rawBlocks.length) {
        const nextLines = getLines(rawBlocks[index]);
        if (nextLines.length > 0 && nextLines.every(isSectionMetaLine)) {
          metaLines.push(...nextLines);
          index += 1;
          continue;
        }
        break;
      }

      let bullets: string[] | undefined;
      if (index < rawBlocks.length) {
        const nextBlock = parseContentBlock(rawBlocks[index]);
        const nextTitle =
          nextBlock.type === 'section'
            ? nextBlock.title
            : nextBlock.type === 'bullets' && nextBlock.intro
              ? nextBlock.intro.replace(/:$/, '').trim()
              : '';
        if (isWhatsInsideTitle(nextTitle)) {
          if (nextBlock.type === 'section' && nextBlock.bullets?.length) {
            bullets = nextBlock.bullets;
            index += 1;
          } else if (nextBlock.type === 'bullets' && nextBlock.items.length) {
            bullets = nextBlock.items;
            index += 1;
          }
        }
      }

      blocks.push({
        type: 'reportDetail',
        body: metaLines.join('\n'),
        bullets,
      });
      continue;
    }

    if (/^\d+\.\s/.test(lines[0] ?? '')) {
      const steps: GuideStep[] = [];
      while (index < rawBlocks.length) {
        const stepLines = getLines(rawBlocks[index]);
        if (!/^\d+\.\s/.test(stepLines[0] ?? '')) {
          break;
        }
        steps.push(parseGuideStepBlock(rawBlocks[index]));
        index += 1;
      }
      blocks.push({ type: 'guide', steps });
      continue;
    }

    if (index + 1 < rawBlocks.length) {
      const nextLines = getLines(rawBlocks[index + 1]);
      if (lines.length > 0 && !lines[0].startsWith('•') && nextLines.every((line) => line.startsWith('•'))) {
        const items = nextLines.map(stripBulletPrefix);
        if (hasLabeledBullets(items)) {
          blocks.push({ type: 'reference', intro: block, items });
          index += 2;
          continue;
        }
      }
    }

    const bulletIndex = lines.findIndex((line) => line.startsWith('•'));
    if (bulletIndex > 0 && !lines[0]?.endsWith(':')) {
      const items = lines.slice(bulletIndex).map(stripBulletPrefix);
      if (hasLabeledBullets(items)) {
        blocks.push({
          type: 'reference',
          intro: lines.slice(0, bulletIndex).join('\n'),
          items,
        });
        index += 1;
        continue;
      }
    }

    if (lines.length === 1 && /^\d+\.\s/.test(lines[0])) {
      const steps: string[] = [];
      while (index < rawBlocks.length) {
        const stepLines = getLines(rawBlocks[index]);
        if (stepLines.length === 1 && /^\d+\.\s/.test(stepLines[0])) {
          steps.push(stripStepPrefix(stepLines[0]));
          index += 1;
        } else {
          break;
        }
      }
      blocks.push({ type: 'steps', steps });
      continue;
    }

    blocks.push(parseContentBlock(block));
    index += 1;
  }

  return blocks;
};

function ReportSectionList({ items }: { items: string[] }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#FFFFFF',
      }}
    >
      {items.map((item, index) => {
        const { label, description } = parseLabeledItem(item);

        return (
          <Box
            key={item}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'auto 1fr', sm: '36px 160px 1fr' },
              alignItems: 'start',
              gap: { xs: 1.25, sm: 2 },
              px: 1.75,
              py: 1.35,
              borderTop: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: theme.palette.background.input,
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.main}12`,
                color: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {index + 1}
            </Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.45,
                color: 'text.primary',
                fontWeight: 600,
                gridColumn: { xs: 2, sm: 'auto' },
              }}
            >
              {label}
            </Typography>
            {description ? (
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.55,
                  color: 'text.secondary',
                  fontSize: '0.8125rem',
                  gridColumn: { xs: '1 / -1', sm: 'auto' },
                  pl: { xs: '40px', sm: 0 },
                }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function ReportDetailCard({
  body,
  bullets,
  sectionsHeading,
}: {
  body: string;
  bullets?: string[];
  sectionsHeading: string;
}) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? theme.shadows[2]
            : '0 4px 16px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: bullets?.length ? `1px solid ${theme.palette.divider}` : 'none',
          bgcolor: theme.palette.background.elevated,
        }}
      >
        <SectionMetaBody body={body} variant="grid" />
      </Box>

      {bullets?.length ? (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: 'text.secondary',
              mb: 1.25,
              lineHeight: 1.2,
            }}
          >
            {sectionsHeading}
          </Typography>
          <ReportSectionList items={bullets} />
        </Box>
      ) : null}
    </Paper>
  );
}

function ReportCard({
  title,
  body,
  bullets,
  sectionsHeading,
  SectionIcon,
}: {
  title: string;
  body?: string;
  bullets?: string[];
  sectionsHeading: string;
  SectionIcon: SvgIconComponent;
}) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        borderInlineStart: `4px solid ${theme.palette.primary.main}`,
        bgcolor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? theme.shadows[2]
            : '0 4px 16px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#FFFFFF',
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.primary.main}08 100%)`,
            border: `1px solid ${theme.palette.primary.main}28`,
            color: 'primary.main',
          }}
        >
          <SectionIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.0625rem' },
            lineHeight: 1.35,
            color: 'text.primary',
            m: 0,
          }}
        >
          {title}
        </Typography>
      </Box>

      {body ? (
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: bullets?.length ? `1px solid ${theme.palette.divider}` : 'none',
            bgcolor: theme.palette.background.elevated,
          }}
        >
          <SectionMetaBody body={body} variant="grid" />
        </Box>
      ) : null}

      {bullets?.length ? (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: 'text.secondary',
              mb: 1.25,
              lineHeight: 1.2,
            }}
          >
            {sectionsHeading}
          </Typography>
          <ReportSectionList items={bullets} />
        </Box>
      ) : null}
    </Paper>
  );
}

function SubBulletList({ items }: { items: string[] }) {
  const theme = useTheme();

  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        mt: 1.25,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);

        return (
          <Box
            component="li"
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              pl: 1.5,
              borderLeft: `2px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: 'text.primary', fontWeight: 500 }}>
                {label}
              </Typography>
              {description ? (
                <Typography variant="body2" sx={{ mt: 0.25, lineHeight: 1.55, color: 'text.secondary' }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function GuideStepList({ steps }: { steps: GuideStep[] }) {
  const theme = useTheme();
  const { i18n } = useTranslation('translation', { i18n: helpI18n });
  const isArabic = i18n.language.startsWith('ar');
  const heading = isArabic ? 'خطوات التنزيل' : 'Download steps';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: '0.8125rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'primary.main',
          mb: 1.5,
        }}
      >
        {heading}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {steps.map((step, stepIndex) => (
          <Box
            key={`${stepIndex}-${step.title}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              p: 1.25,
              borderRadius: '10px',
              bgcolor: theme.palette.background.elevated,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.main}18`,
                color: 'primary.main',
                fontSize: '0.8125rem',
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {stepIndex + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.45, color: 'text.primary' }}>
                {step.title}
              </Typography>
              {step.body ? (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, lineHeight: 1.6, color: 'text.secondary', whiteSpace: 'pre-line' }}
                >
                  {step.body}
                </Typography>
              ) : null}
              {step.bullets?.length ? <SubBulletList items={step.bullets} /> : null}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function StatusReferenceCard({ intro, items }: { intro: string; items: string[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CalloutBox variant="info" body={intro} />
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        <LabeledOptionList items={items} grid />
      </Paper>
    </Box>
  );
}

function StepList({ steps }: { steps: string[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {steps.map((step, stepIndex) => (
        <Box
          key={`${stepIndex}-${step.slice(0, 24)}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 1.5,
            borderRadius: '12px',
            bgcolor: theme.palette.background.elevated,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${theme.palette.primary.main}18`,
              color: 'primary.main',
              fontSize: '0.8125rem',
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {stepIndex + 1}
          </Box>
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.75, color: 'text.primary', pt: 0.25, whiteSpace: 'pre-line' }}
          >
            {step}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function FillLevelIndicator({ percent }: { percent: number }) {
  const theme = useTheme();

  return (
    <Box
      aria-hidden
      sx={{
        width: 44,
        height: 56,
        borderRadius: '10px',
        border: `2px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.input,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${percent}%`,
          bgcolor: 'primary.main',
          opacity: theme.palette.mode === 'dark' ? 0.9 : 0.82,
          transition: 'height 0.2s ease',
        }}
      />
    </Box>
  );
}

function FillLevelOptionList({ items }: { items: string[] }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.25,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);
        const percent = getFillLevelPercent(label) ?? 0;

        return (
          <Box
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.elevated,
            }}
          >
            <FillLevelIndicator percent={percent} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.45, color: 'text.primary' }}>
                {label}
              </Typography>
              {description ? (
                <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.55, color: 'text.secondary' }}>
                  {description}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.55, color: 'text.secondary' }}>
                  {percent}%
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function LabeledOptionList({ items, grid = false }: { items: string[]; grid?: boolean }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: grid ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
        gap: 1.25,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);

        return (
          <Box
            key={item}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              borderInlineStart: `3px solid ${theme.palette.primary.main}`,
              bgcolor: theme.palette.background.elevated,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.45, color: 'text.primary' }}>
              {label}
            </Typography>
            {description ? (
              <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.55, color: 'text.secondary' }}>
                {description}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function BulletList({ intro, items }: { intro?: string; items: string[] }) {
  const useFillLevel = isFillLevelBulletSet(items);
  const useLabeled = !useFillLevel && hasLabeledBullets(items);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {intro ? (
        <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.primary', whiteSpace: 'pre-line' }}>
          {intro}
        </Typography>
      ) : null}
      {useFillLevel ? (
        <FillLevelOptionList items={items} />
      ) : useLabeled ? (
        <LabeledOptionList items={items} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                  mt: '0.72em',
                }}
              />
              <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.primary', flex: 1 }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CalloutBox({
  variant,
  title,
  body,
  bullets,
}: {
  variant: CalloutVariant;
  title?: string;
  body?: string;
  bullets?: string[];
}) {
  const theme = useTheme();

  const palette = {
    info: {
      bg: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : '#EFF6FF',
      border: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.35)' : '#BFDBFE',
      color: theme.palette.mode === 'dark' ? '#93C5FD' : '#1D4ED8',
      Icon: InfoOutlinedIcon,
    },
    warning: {
      bg: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.12)' : '#FFFBEB',
      border: theme.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.35)' : '#FDE68A',
      color: theme.palette.mode === 'dark' ? '#FCD34D' : '#B45309',
      Icon: WarningAmberOutlinedIcon,
    },
    tip: {
      bg: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : '#F0FDF4',
      border: theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.3)' : '#BBF7D0',
      color: theme.palette.mode === 'dark' ? '#86EFAC' : '#15803D',
      Icon: LightbulbOutlinedIcon,
    },
  }[variant];

  const Icon = palette.Icon;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '14px',
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Icon sx={{ fontSize: 20, color: palette.color, mt: 0.15, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: palette.color, mb: body || bullets?.length ? 1 : 0 }}>
              {title}
            </Typography>
          ) : null}
          {body ? (
            <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.primary', whiteSpace: 'pre-line' }}>
              {body}
            </Typography>
          ) : null}
          {bullets?.length ? (
            <Box sx={{ mt: body ? 1.25 : 0 }}>
              <BulletList items={bullets} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function SectionCard({
  title,
  body,
  steps,
  bullets,
}: {
  title: string;
  body?: string;
  steps?: string[];
  bullets?: string[];
}) {
  const theme = useTheme();
  const { i18n } = useTranslation('translation', { i18n: helpI18n });
  const SectionIcon = getSectionIcon(title);
  const hasContent = Boolean(body || steps?.length || bullets?.length);
  const isReportCard = Boolean(SectionIcon);
  const isArabic = i18n.language.startsWith('ar');
  const sectionsHeading = isArabic ? 'أقسام التقرير' : 'Report sections';

  if (isReportCard && SectionIcon) {
    return (
      <ReportCard
        title={title}
        body={body}
        bullets={bullets}
        sectionsHeading={sectionsHeading}
        SectionIcon={SectionIcon}
      />
    );
  }

  if (title && !hasContent) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: 0.5 }}>
        {SectionIcon ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: `${theme.palette.primary.main}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SectionIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
        ) : null}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: '1.0625rem',
            lineHeight: 1.4,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      {title || body ? (
        <Box
          sx={{
            p: 2,
            pb: steps?.length || bullets?.length ? 1.75 : 2,
            borderBottom: steps?.length || bullets?.length ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {SectionIcon ? (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '11px',
                  bgcolor: `${theme.palette.primary.main}14`,
                  border: `1px solid ${theme.palette.primary.main}22`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SectionIcon sx={{ fontSize: 22, color: 'primary.main' }} />
              </Box>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {title ? (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.0125rem',
                    lineHeight: 1.35,
                    color: 'text.primary',
                  }}
                >
                  {title}
                </Typography>
              ) : null}
              {body ? (
                <Box sx={{ mt: title ? 1.25 : 0 }}>
                  <SectionMetaBody body={body} />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : null}
      {steps?.length ? (
        <Box sx={{ p: 2, pt: title || body ? 0 : 2 }}>
          <StepList steps={steps} />
        </Box>
      ) : null}
      {bullets?.length ? (
        <Box sx={{ px: 2, pb: 2, pt: steps?.length ? 0 : title || body ? 0 : 2 }}>
          {isFillLevelBulletSet(bullets) ? (
            <FillLevelOptionList items={bullets} />
          ) : hasLabeledBullets(bullets) ? (
            <SubBulletList items={bullets} />
          ) : (
            <BulletList items={bullets} />
          )}
        </Box>
      ) : null}
    </Paper>
  );
}

interface ArticleDetailContentProps {
  content: string;
}

const ArticleDetailContent: React.FC<ArticleDetailContentProps> = ({ content }) => {
  const { fontFamily, bodySx } = useHelpTypography();
  const { i18n } = useTranslation('translation', { i18n: helpI18n });
  const blocks = parseArticleContent(content);
  const isArabic = i18n.language.startsWith('ar');
  const reportSectionsHeading = isArabic ? 'أقسام التقرير' : 'Report sections';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: 880,
        width: '100%',
        fontFamily,
        '& .MuiTypography-root': { fontFamily: 'inherit' },
      }}
    >
      {blocks.map((block, index) => {
        const nextBlock = blocks[index + 1];
        const isLastBlock = index === blocks.length - 1;

        switch (block.type) {
          case 'guide':
            return <GuideStepList key={`guide-${index}`} steps={block.steps} />;
          case 'reference':
            return (
              <StatusReferenceCard
                key={`reference-${index}`}
                intro={block.intro}
                items={block.items}
              />
            );
          case 'reportDetail':
            return (
              <ReportDetailCard
                key={`report-detail-${index}`}
                body={block.body}
                bullets={block.bullets}
                sectionsHeading={reportSectionsHeading}
              />
            );
          case 'section':
            return (
              <SectionCard
                key={`section-${index}`}
                title={block.title}
                body={block.body}
                steps={block.steps}
                bullets={block.bullets}
              />
            );
          case 'steps':
            return (
              <SectionCard key={`steps-${index}`} title="" steps={block.steps} />
            );
          case 'bullets':
            return (
              <Paper
                key={`bullets-${index}`}
                elevation={0}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: '16px',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                  boxShadow: theme.shadows[1],
                })}
              >
                <BulletList intro={block.intro} items={block.items} />
              </Paper>
            );
          case 'callout':
            return (
              <CalloutBox
                key={`callout-${index}`}
                variant={block.variant}
                title={block.title}
                body={block.body}
                bullets={block.bullets}
              />
            );
          case 'paragraph':
          default:
            if (isSectionMetaLine(block.text) || getLines(block.text).every(isSectionMetaLine)) {
              return (
                <ReportDetailCard
                  key={`paragraph-meta-${index}`}
                  body={getLines(block.text).join('\n')}
                  sectionsHeading={reportSectionsHeading}
                />
              );
            }

            if (/^(tip:|نصيحة)/i.test(block.text.trim())) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="tip" body={block.text} />
              );
            }

            if (nextBlock && isOptionsSection(nextBlock)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="info" body={block.text} />
              );
            }

            if (isLastBlock && blocks.length > 1 && isTipParagraph(block.text)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="tip" body={block.text} />
              );
            }

            if (nextBlock?.type === 'bullets' && hasLabeledBullets(nextBlock.items)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="info" body={block.text} />
              );
            }

            return (
              <Typography
                key={`paragraph-${index}`}
                variant="body1"
                sx={{
                  ...bodySx,
                  lineHeight: 1.8,
                  color: 'text.primary',
                  whiteSpace: 'pre-line',
                  fontFamily: 'inherit',
                }}
              >
                {block.text}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default ArticleDetailContent;
